import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import { useModalEscape } from '../hooks/useModalEscape';

const MobileModal = ({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  saveLabel = "Save",
  saveDisabled = false,
  showSaveButton = true,
  className = "",
  // [UI-MOBILEMODAL-ADOPTION wave 1 — 2026-07-04]: overlayClassName lands on the
  // OUTER fixed shell in BOTH branches (className only reached the desktop card,
  // so z-index overrides like z-[70] silently failed on desktop). ariaLabel
  // covers JSX-fragment titles; string titles auto-label.
  overlayClassName = "",
  ariaLabel,
  // [UI-MOBILEMODAL-ADOPTION wave 2a — 2026-07-05]: test-hook passthrough. Fixes the
  // long-standing quirk where MobileModal ignored a data-testid prop (EditItemDialog
  // passed one that never reached the DOM). Migrated dialogs keep their existing chrome
  // testids — dialog root (data-testid), Save (saveTestId), Close (closeTestId) — so
  // behavior suites pass unchanged. All optional; omit for none.
  'data-testid': dataTestId,
  saveTestId,
  closeTestId,
  // [UI-MOBILEMODAL-ADOPTION wave 2b — 2026-07-05]: opt-in backdrop-click dismissal.
  // Default OFF so every already-migrated dialog is behavior-unchanged. When ON, a click
  // on the DESKTOP overlay itself (not its card/children) fires onClose — restoring the
  // affordance the hand-rolled *ViewModal read modals had before migration. Desktop-only
  // by design: the mobile branch is full-screen with no backdrop to click. Reusable
  // enabler for later waves (2c/2d) whose modals also backdrop-close.
  closeOnBackdrop = false,
  // [UI-MOBILEMODAL-ADOPTION wave 2c — 2026-07-05]: desktop card max-width override.
  // Default 'max-w-2xl' (the historic hardcoded width — every prior-wave dialog is
  // unchanged). Wave-2c dialogs range from tight confirms (max-w-md) to wide data grids
  // (max-w-4xl/5xl); this lets each keep its intended desktop width instead of being
  // squashed or stretched to 2xl. DESKTOP-only: the mobile branch is full-screen (no
  // max-width). Pass a single Tailwind max-w-* class.
  maxWidthClass = 'max-w-2xl',
  // [UI-MOBILEMODAL-ADOPTION wave 2c-2 — 2026-07-09]: gradient-banner enabler. When set,
  // REPLACES the header bar's chrome classes (bg/border) on BOTH branches so identity
  // gradients like 'bg-gradient-to-r from-amber-800 to-orange-800 border-b border-amber-700'
  // apply without Tailwind same-property conflicts against the defaults. Structural
  // classes (flex layout + p-4/p-6 padding + safe-area) are NOT overridable — only chrome.
  // Card-level identity (body gradients, colored borders) rides the existing className
  // prop; rounded top corners come free from the desktop card's overflow-hidden.
  // Default '' → every prior-wave dialog byte-identical.
  headerClassName = '',
  // [UI-MOBILEMODAL-ADOPTION dismissible — 2026-07-09]: blocking-gate enabler
  // (MOBILEMODAL-DISMISSIBLE-PROP). When false, the Close(X) button is not rendered on
  // either branch, the Escape registration is suppressed (useModalEscape sees
  // isOpen && dismissible), and closeOnBackdrop is ignored — the modal can only close
  // through its own body CTAs. For blocking gates like ConsentPrompt (legal consent)
  // and WelcomePopup (first-run path choice) where dismissal must not exist.
  // Default true → every prior-wave dialog byte-identical.
  dismissible = true
}) => {
  const isMobile = useIsMobile();
  const resolvedAriaLabel = ariaLabel || (typeof title === 'string' ? title : undefined);
  // Close only when the click lands on the overlay itself, never when it bubbles up
  // from the card/content (e.target === e.currentTarget guards that).
  const handleBackdropClick = (closeOnBackdrop && dismissible)
    ? (e) => { if (e.target === e.currentTarget) onClose(); }
    : undefined;

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // [APR 2026]: Standardize Escape dismissal via useModalEscape hook.
  // [dismissible 2026-07-09]: non-dismissible gates pass a hard false so the
  // listener never registers (the hook itself no-ops on a falsy first arg).
  useModalEscape(isOpen && dismissible, onClose);

  if (!isOpen) return null;

  if (isMobile) {
    // Mobile: Full-screen modal
    return (
      <div
        className={`fixed inset-0 bg-gray-900 z-50 flex flex-col ${overlayClassName} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={resolvedAriaLabel}
        data-testid={dataTestId}
      >
        {/* Mobile Header */}
        {/* [UI-MOBILEMODAL-ADOPTION B-7 2026-07-04]: honor the notch — viewport-fit=cover
            is set (public/index.html) and the app shell + bottom nav already pad by
            env(safe-area-inset-*); this full-screen header was the one surface that
            didn't, so the close/save row could sit under the status bar on device. */}
        <div
          className={`flex items-center justify-between p-4 ${headerClassName || 'border-b border-gray-700 bg-gray-800'}`}
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 1rem)' }}
        >
          {dismissible && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close modal"
              data-testid={closeTestId}
            >
              <X size={20} className="text-gray-300" />
            </button>
          )}

          <h2 className="font-bold text-white text-lg truncate px-4 text-center flex-1">
            {title}
          </h2>

          {showSaveButton && (
            <button
              onClick={onSave}
              disabled={saveDisabled}
              className={`px-4 py-2 rounded-lg font-medium min-h-[44px] transition-colors ${
                saveDisabled
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              data-testid={saveTestId}
            >
              {saveLabel}
            </button>
          )}
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-y-auto bg-gray-900">
          <div className="p-4">
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Centered modal with backdrop
  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${overlayClassName}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-gray-800 rounded-lg w-full ${maxWidthClass} max-h-[90vh] overflow-hidden shadow-2xl ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={resolvedAriaLabel}
        data-testid={dataTestId}
      >
        {/* Desktop Header */}
        <div className={`flex items-center justify-between p-6 ${headerClassName || 'border-b border-gray-700'}`}>
          <h2 className="text-xl font-bold text-white truncate pr-4">
            {title}
          </h2>

          <div className="flex items-center gap-2">
            {showSaveButton && (
              <button
                onClick={onSave}
                disabled={saveDisabled}
                className={`px-4 py-2 rounded-lg font-medium min-h-[44px] transition-colors ${
                  saveDisabled
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                data-testid={saveTestId}
              >
                {saveLabel}
              </button>
            )}

            {dismissible && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close modal"
                data-testid={closeTestId}
              >
                <X size={20} className="text-gray-300" />
              </button>
            )}
          </div>
        </div>

        {/* Desktop Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileModal;
